import * as XLSX from 'xlsx';
import React, { useState, useEffect } from 'react';
import './ContainerSearch.css';
import containerAPI from '../../services/containerAPI';
import { validateContainerNumber, calculateCheckDigit } from '../../utils/ContainerNumberValidator';
import LoadingSpinner from '../common/LoadingSpinner';
import { decodeJWT } from '../../utils/jwt';

const ContainerSearch = () => {
  const [containers, setContainers] = useState([]);
  const [filteredContainers, setFilteredContainers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [sortBy, setSortBy] = useState('updatedAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [deletingId, setDeletingId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pendingDeleteNumber, setPendingDeleteNumber] = useState(null);
  const [statusOptions, setStatusOptions] = useState([{ value: 'ALL', label: 'All Statuses' }]);
  const [statusOptionsLoading, setStatusOptionsLoading] = useState(false);
  // Delete handler
  const handleDelete = async (containerNumber) => {
    setDeletingId(containerNumber);
    setShowDeleteModal(false);
    try {
      const token = sessionStorage.getItem('agentToken');
      const agent = decodeJWT(token);
      const companyId = agent && agent.companyId ? agent.companyId : 1;
      await containerAPI.delete(containerNumber, companyId);
      await loadContainers();
    } catch (error) {
      alert('Delete failed: ' + (error?.response?.data?.message || error.message));
    } finally {
      setDeletingId(null);
      setPendingDeleteNumber(null);
    }
  };

    // Excel download handler
  const handleDownloadExcel = () => {
    if (!filteredContainers.length) return;
    const columnsToRemove = [
      'company_id', 'active', 'createdAt', 'updatedAt', 'milestones', 'updateLogs', 'companyId', 'id'
    ];
    const dataToExport = filteredContainers.map(row => {
      const newRow = { ...row };
      columnsToRemove.forEach(col => delete newRow[col]);
      return newRow;
    });
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Containers');
    XLSX.writeFile(workbook, 'ContainerSearch.xlsx');
  };
  
  const openDeleteModal = (containerNumber) => {
    setPendingDeleteNumber(containerNumber);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setPendingDeleteNumber(null);
  };

  useEffect(() => {
    loadContainers();

    // Fetch status options from backend
    const fetchStatusOptions = async () => {
      setStatusOptionsLoading(true);
      try {
        const response = await containerAPI.getStatusOptions(); // implement this in containerAPI
        // Assuming response.data is an array of { code, displayName }
        const options = [
          { value: 'ALL', label: 'All Statuses' },
          ...response.data.map(opt => ({
            value: opt.statusId,
            label: opt.statusDesc || opt.statusId
          }))
        ];
        setStatusOptions(options);
      } catch (error) {
        setStatusOptions([{ value: 'ALL', label: 'All Statuses' }]);
      } finally {
        setStatusOptionsLoading(false);
      }
    };
    fetchStatusOptions();
  }, []);

  useEffect(() => {
    filterAndSortContainers();
  }, [containers, searchTerm, statusFilter, sortBy, sortOrder]);

  const loadContainers = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('agentToken');
      const agent = decodeJWT(token);
      const companyId = agent && agent.companyId ? agent.companyId : 1;
      const response = await containerAPI.getAll(companyId);
      setContainers(response.data);
    } catch (error) {
      console.error('Error loading containers:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortContainers = () => {
    let filtered = [...containers];

    // Apply search filter
    if (searchTerm.trim()) {
      filtered = filtered.filter(container =>
        container.containerNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        container.bookingNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        container.vesselName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        container.shippingLine?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Container number validation
    if (searchTerm && /^[A-Z]{4}[0-9]{7}$/.test(searchTerm) && !validateContainerNumber(searchTerm)) {
      setFilteredContainers([]);
      return;
    }

    // Apply status filter
    if (statusFilter !== 'ALL') {
      let statusText = statusOptions.find(option => option.value == statusFilter)?.label;
      filtered = filtered.filter(container =>
        container.status === statusText
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      if (sortBy === 'updatedAt' || sortBy === 'createdAt') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredContainers(filtered);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) return '↕️';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  return (
    <div className="container-search">
      <div className="search-header">
        <h3>Container Search</h3>
        <p>Search and filter containers in the system</p>
      </div>

      <div className="search-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by container, booking, vessel, or shipping line..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filters">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            disabled={statusOptionsLoading}
          >
            {statusOptions.map((option, idx) => (
              <option
                key={
                  (option.value !== undefined && option.label !== undefined)
                    ? `${option.value}-${option.label}`
                    : `option-${idx}`
                }
                value={option.value}
              >
                {option.label}
              </option>
            ))}
          </select>

          <button onClick={loadContainers} className="btn btn-secondary">
            Refresh
          </button>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
        <button className="btn btn-success" onClick={handleDownloadExcel} disabled={!filteredContainers.length}>
          Download Excel
        </button>
      </div>
      <div className="results-summary">
        <span>
          Showing {filteredContainers.length} of {containers.length} containers
        </span>
      </div>
      {loading ? (
        <LoadingSpinner text="Loading containers..." />
      ) : (
        <div className="table-container" style={{ maxHeight: '500px', overflowY: 'auto', overflowX: 'auto' }}>
          <table className="table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead className="sticky-header">
              <tr>
                <th onClick={() => handleSort('containerNumber')}>
                  Container {getSortIcon('containerNumber')}
                </th>
                <th onClick={() => handleSort('bookingNumber')}>
                  Booking {getSortIcon('bookingNumber')}
                </th>
                <th onClick={() => handleSort('status')}>
                  Status {getSortIcon('status')}
                </th>
                <th>Vessel / Voyage</th>
                <th>Route</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredContainers.map(container => (
                <tr key={container.id} className="container-row">
                  <td>
                    <div className="container-number">
                      {container.containerNumber}
                    </div>
                    <div className="container-type">
                      {container.size} {container.type}
                    </div>
                  </td>
                  <td>
                    <div className="booking-number">
                      {container.bookingNumber}
                    </div>
                    <div className="shipping-line">
                      {container.shippingLine}
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${container.status}`}>
                      {container.status}
                    </span>
                  </td>
                  <td>
                    <div className="vessel-info">
                      <div className="vessel-name">
                        {container.vesselName || 'N/A'}
                      </div>
                      <div className="voyage-number">
                        {container.voyageNumber || ''}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="route-info">
                      <div className="route">
                        {container.portOfLoading} → {container.portOfDischarge}
                      </div>
                      <div className="dates">
                        ETD: {container.etd
                          ? new Date(container.etd).toLocaleDateString()
                          : 'N/A'}
                      </div>
                    </div>
                  </td>
                  <td>
                    <button
                      title="Delete Container"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginTop: '0.3em' }}
                      onClick={e => { e.preventDefault(); openDeleteModal(container.containerNumber); }}
                      disabled={deletingId === container.containerNumber}
                    >
                      {deletingId === container.containerNumber
                        ? <span style={{ color: 'red', fontSize: '1.2em' }}>⏳</span>
                        : <span style={{ color: 'red', fontSize: '1.2em' }}>&#128465;</span>}
                    </button>
      {showDeleteModal && (
        <div className="container-search-modal-bg">
          <div className="container-search-modal">
            <h4>Delete Container</h4>
            <p>Are you sure you want to delete this container?</p>
            <div className="modal-actions">
              <button onClick={closeDeleteModal}>Cancel</button>
              <button className="delete-btn" onClick={() => handleDelete(pendingDeleteNumber)}>Delete</button>
            </div>
          </div>
        </div>
      )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredContainers.length === 0 && (
            <div className="empty-state">
              <p>No containers found matching your search criteria</p>
            </div>
          )}
        </div>
      )}

  <style>{`
        .sticky-header th {
          position: sticky;
          top: 0;
          background: #f8f9fa;
          z-index: 2;
        }
        .container-search {
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          padding: 2rem;
        }
        
        .search-header {
          margin-bottom: 2rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid #eee;
        }
        
        .search-header h3 {
          margin: 0 0 0.5rem 0;
          color: #333;
        }
        
        .search-header p {
          margin: 0;
          color: #666;
        }
        
        .search-controls {
          display: flex;
          gap: 1rem;
          margin-bottom: 1.5rem;
          align-items: flex-end;
        }
        
        .search-box {
          flex: 1;
        }
        
        .search-box input {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 1rem;
        }
        
        .filters {
          display: flex;
          gap: 1rem;
          align-items: center;
        }
        
        .filters select {
          padding: 0.75rem;
          border: 1px solid #ddd;
          border-radius: 4px;
          min-width: 150px;
        }
        
        .results-summary {
          margin-bottom: 1rem;
          color: #666;
          font-size: 0.9rem;
        }
        
        .table-scroll-container {
          width: 100%;
          overflow-x: auto;
        }
        
        .table {
          min-width: 900px;
          width: 100%;
          border-collapse: collapse;
        }
        
        .table th {
          cursor: pointer;
          user-select: none;
          background: #f8f9fa;
          padding: 1rem;
          font-weight: 600;
          color: #555;
          border-bottom: 1px solid #eee;
        }
        
        .table th:hover {
          background: #e9ecef;
        }
        
        .table td {
          padding: 1rem;
          border-bottom: 1px solid #eee;
          vertical-align: top;
        }
        
        .container-row:hover {
          background: #f8f9fa;
        }
        
        .container-number {
          font-weight: 600;
          color: #333;
          margin-bottom: 0.25rem;
        }
        
        .container-type {
          color: #666;
          font-size: 0.8rem;
        }
        
        .booking-number {
          font-weight: 500;
          color: #333;
          margin-bottom: 0.25rem;
        }
        
        .shipping-line {
          color: #666;
          font-size: 0.8rem;
        }
        
        .vessel-info, .route-info, .update-info {
          font-size: 0.9rem;
        }
        
        .vessel-name {
          font-weight: 500;
          margin-bottom: 0.25rem;
        }
        
        .voyage-number {
          color: #666;
        }
        
        .route {
          font-weight: 500;
          margin-bottom: 0.25rem;
        }
        
        .dates {
          color: #666;
          font-size: 0.8rem;
        }
        
        .empty-state {
          text-align: center;
          padding: 3rem;
          color: #666;
        }
        
        @media (max-width: 768px) {
          .search-controls {
            flex-direction: column;
            align-items: stretch;
          }
          
          .filters {
            justify-content: space-between;
          }
          
          .table-scroll-container {
            overflow-x: auto;
          }
          
          .table {
            min-width: 800px;
          }
        }
      `}</style>
    </div>
  );
};

export default ContainerSearch;